import { type BubbleMenuPluginProps } from '@tiptap/extension-bubble-menu'
import { EditorState, NodeSelection } from '@tiptap/pm/state'
import { Content, useEditorState } from '@tiptap/react'
import { BubbleMenu } from '@tiptap/react/menus'
import {
  Fragment,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { User } from '@opallabs/concepts'
import { getScrollableParent } from '@opallabs/lambda'
import { Button, ButtonSize, Icon } from '@opallabs/lapidary'
import {
  getActiveTextAlign,
  getAlignIcon,
  getHeadingIcon,
  getListIcon,
} from '@opallabs/text-editor/src/TextEditor/utils'
import { useTextEditor } from '../../../TextEditor'
import { ControlButtonPopover } from '../../../TextEditor/components/ControlBar'
import { Thread } from '../../../Thread'
import { ThreadContextProvider } from '../../../Thread/useThread'
import { CollaborationTextEditorProps } from '../../types'
import { hasUnresolvedThread } from '../../utils/hasUnresolvedThread'
import {
  AlignButtons,
  FormattingButtons,
  HeadingButtons,
  ListButtons,
} from '../FullControlBar/ButtonGroups'
import * as styles from './styles'

export type SelectionMenuProps = {
  /**
   * The resource the comment thread or mention will be associated with.
   * This is to enable notifications. Needed for comments and mentions to be enabled.
   */
  resource?: CollaborationTextEditorProps['resource']
  /**
   * The user who is currently logged in. This is used to associate comments with
   * the user who made them and assist with notifications. Needed for comments to be enabled.
   */
  user?: User | null
  commentsPending: boolean
}

const isNodeSelection = (state: EditorState): boolean =>
  state.selection instanceof NodeSelection

const shouldShow: BubbleMenuPluginProps['shouldShow'] = ({
  from,
  to,
  state,
  editor,
}) =>
  to - from > 0 &&
  // Don't show for a node selection that has an unresolved comment thread
  (!isNodeSelection(state) || !hasUnresolvedThread(editor, { from, to }))

export const SelectionMenu = ({
  user,
  resource,
  commentsPending,
}: SelectionMenuProps) => {
  const { editor, editorElement } = useTextEditor()
  const bubbleMenuRef = useRef<HTMLDivElement>(null)
  const [commentPopoverOpen, setCommentPopoverOpen] = useState(false)
  const {
    isInitialized,
    activeHeadingLevel,
    activeTextAlign,
    activeListType,
    selectionHasUnresolvedThread,
  } = useEditorState({
    editor,
    selector: (snapshot) => ({
      selectionHasUnresolvedThread: hasUnresolvedThread(
        snapshot.editor,
        snapshot.editor.state.selection
      ),
      isInitialized: snapshot.editor.isInitialized,
      activeHeadingLevel: [1, 2, 3, 4].find((level) =>
        snapshot.editor.isActive('heading', { level })
      ),
      activeTextAlign: getActiveTextAlign(snapshot.editor.state, 'left'),
      activeListType: ['bulletList', 'orderedList', 'taskList'].find((type) =>
        snapshot.editor.isActive(type)
      ),
    }),
  })

  // Updates position of bubbleMenu when switching into CommentEditor
  // to account for the menu's size variance.
  useLayoutEffect(() => {
    if (!commentPopoverOpen) return

    editor.commands.setMeta('bubbleMenu', 'updatePosition')
  }, [commentPopoverOpen, editor])

  const handleSubmitComment = useCallback(
    (input: Content) => {
      if (!resource || !user) return

      editor
        .chain()
        .focus()
        .setThread({
          content: input,
          data: {
            resource_id: resource.id,
            resource_type: resource.type,
            author_id: user.uuid,
          },
          commentData: {
            author_id: user.uuid,
            userName: user.name,
            userAvatar: user.asset?.url,
            pending: commentsPending,
          },
        })
        .command(({ state, commands }) =>
          // Reset selection to the head of the thread
          // This will close the selection menu
          commands.setTextSelection(state.selection.head)
        )
        .run()

      setCommentPopoverOpen(false)
    },
    [editor, resource, user, commentsPending, setCommentPopoverOpen]
  )

  const menuOptions = useMemo<BubbleMenuPluginProps['options']>(
    () => ({
      flip: {
        fallbackPlacements: ['bottom'],
        boundary: editorElement,
      },
      onShow: () => {
        if (!bubbleMenuRef.current) return
        bubbleMenuRef.current.style.zIndex = '0'
      },
      onHide: () => setCommentPopoverOpen(false),
      placement: 'top',
      scrollTarget: getScrollableParent('y', editorElement, false),
    }),
    [editorElement, setCommentPopoverOpen]
  )

  if (!isInitialized) {
    return null
  }

  return (
    <BubbleMenu
      ref={bubbleMenuRef}
      editor={editor}
      options={menuOptions}
      shouldShow={shouldShow}
      resizeDelay={0}
    >
      {!commentPopoverOpen ? (
        <div css={styles.bubbleMenu}>
          <ControlButtonPopover
            align="start"
            label="Format Options"
            icon={
              <Icon glyph={getHeadingIcon(activeHeadingLevel)} ariaHidden />
            }
          >
            <HeadingButtons />
          </ControlButtonPopover>
          <span css={styles.separator} />
          <FormattingButtons />
          <span css={styles.separator} />
          <ControlButtonPopover
            align="end"
            label="Align Options"
            icon={<Icon glyph={getAlignIcon(activeTextAlign)} ariaHidden />}
          >
            <AlignButtons activeTextAlign={activeTextAlign} />
          </ControlButtonPopover>
          <ControlButtonPopover
            align="end"
            label="List Options"
            icon={<Icon glyph={getListIcon(activeListType)} ariaHidden />}
          >
            <ListButtons />
          </ControlButtonPopover>
          {resource && user ? (
            <Fragment>
              <span css={styles.separator} />
              <Button
                buttonText="Add Comment"
                name="Add Comment"
                disabled={selectionHasUnresolvedThread}
                icon={<Icon glyph="gem-comment" ariaHidden />}
                hideButtonText={true}
                onClick={() => setCommentPopoverOpen(true)}
                size={ButtonSize.Small}
                variant="secondary"
              />
            </Fragment>
          ) : null}
        </div>
      ) : (
        <ThreadContextProvider
          onSubmitComment={handleSubmitComment}
          onClose={() => setCommentPopoverOpen(false)}
        >
          <Thread resourceType={resource?.type} resourceId={resource?.id} />
        </ThreadContextProvider>
      )}
    </BubbleMenu>
  )
}
